import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet";
import palyazatPdf from "../../assets/Felhivas_OETP_260113_NEU_fin.pdf";
import { COMPANY, SITE_URL } from "../../config/company";
import MultiStepContactForm from "./MultiStepContactForm";
import "./palyazat.scss";

// SEO Configuration for the Palyazat page
const SEO_CONFIG = {
  title:
    "Napelemes Akkumulátor Pályázat 2026 | Akár 2,5 Millió Ft Támogatás | Green Island",
  description:
    "2026-os napelemes akkumulátor pályázat: akár 2,5 millió Ft állami támogatás otthoni energiatárolóra. Regisztráljon most a Green Island Invest Zrt.-nél és legyen az elsők között! 100 milliárd Ft keret, 10 kWh akkumulátor.",
  keywords:
    "napelemes akkumulátor pályázat 2026, akkumulátor támogatás, otthoni energiatároló pályázat, napelem akkumulátor, állami támogatás napelem, Green Island Invest, energiatároló pályázat, napelemes rendszer akkumulátor, háztartási akkumulátor pályázat",
  url: "https://greenislandinvest.hu/palyazat",
  image: "https://greenislandinvest.hu/og-image.jpg",
  siteName: "Green Island Invest Zrt.",
  locale: "hu_HU",
};

const AccordionItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className={`accordion-item ${isOpen ? "open" : ""}`}>
      <button
        className="accordion-header"
        onClick={onClick}
        style={{
          width: "100%",
          textAlign: "left",
          background: "none",
          border: "none",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        {question}
        <span
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}>
          ▼
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}>
            <div className="accordion-body">{answer}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PalyazatModel = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const [submitStatus, setSubmitStatus] = useState(null);

  const [openAccordion, setOpenAccordion] = useState(null);

  const handleAccordionClick = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const faqData = [
    {
      question: "1. Mi ez a pályázat röviden?",
      answer:
        "Lakossági program, aminek célja, hogy a napközben megtermelt napelemes energiát este is fel tudd használni egy otthoni akkumulátor segítségével.",
    },
    {
      question: "2. Mekkora keretről és támogatásról beszélünk?",
      answer:
        "A kormányinfós beszámolók szerint 100 milliárd Ft keret és akár 2,5 millió Ft támogatás szerepel.",
    },
    {
      question: "3. Mekkora akkumulátort céloz a program?",
      answer:
        "A sajtóban megjelent információk alapján tipikusan kb. 10 kWh körüli otthoni tároló szerepel célként (a pontos paraméter a felhívásban lesz).",
    },
    {
      question: "4. Kell hozzá napelem?",
      answer:
        "A kormányinfós beszámoló szerint feltétel, hogy legyen napelemed, és az ahhoz kapcsolódó házi akkumulátorra lehet pályázni.",
    },
    {
      question: "5. Társasházban lakók pályázhatnak?",
      answer:
        "Igen, társasház nem kizáró ok, de legfeljebb 6 lakásos társasház esetén lehet pályázni. Ennél nagyobb társasházak lakói sajnos nem jogosultak.",
    },
    {
      question: "6. Kinek éri meg a legjobban?",
      answer: (
        <ul>
          <li>Aki sokat fogyaszt este, amikor már nem termel a napelem.</li>
          <li>Aki szeretné, hogy a napelem ne „csak nappal legyen hasznos”.</li>
          <li>Aki csökkentené a hálózatról vásárolt áramot.</li>
        </ul>
      ),
    },
    {
      question: "7. Mikor indulhat a beadás?",
      answer:
        "A sajtóhírek szerint januárban/január közepe körül jöhet a felhívás, és a beadás is ehhez igazodhat – a végső dátumot a hivatalos kiírás mondja ki.",
    },
    {
      question: "8. Beérkezési sorrend lesz?",
      answer:
        "Ezt csak a felhívás fogja eldönteni. Viszont a tapasztalat az, hogy a keretprogramoknál a gyors, hibamentes előkészítés mindig előny.",
    },
    {
      question: "9. Tényleg „nullára” viszi a villanyszámlát az akkumulátor?",
      answer:
        "Általában nem nullára, de nagyon sokat tud javítani: a nappal megtermelt áram nagyobb részét estére át tudod vinni, így kevesebbet vásárolsz a hálózatról.",
    },
    {
      question: "10. Minden inverter képes akkumulátort kezelni?",
      answer: (
        <>
          <p>Nem. Röviden:</p>
          <ul>
            <li>
              <strong>Hibrid inverter:</strong> jellemzően alkalmas akkura.
            </li>
            <li>
              <strong>Nem hibrid:</strong> gyakran invertercsere vagy más
              műszaki megoldás kell.
            </li>
          </ul>
          <p>
            Ezért fontos előre tisztázni az inverter típusát és kompatibilitást.
          </p>
        </>
      ),
    },
    {
      question: "11. Áramszünet esetén is lesz áramom, ha lesz akkumulátorom?",
      answer:
        "Nem automatikusan. Ehhez backup / szigetüzem jellegű kialakítás kell (külön villamos megoldásokkal). Hogy ezt a pályázat hogyan kezeli, a felhívásban derül ki.",
    },
    {
      question: "12. Mi szokott a legnagyobb „buktató” lenni?",
      answer: (
        <ul>
          <li>Inverter–akkumulátor kompatibilitás hiánya.</li>
          <li>Elosztóban kevés hely / hiányzó védelem.</li>
          <li>Hiányos fotók/dokumentumok.</li>
          <li>Rosszul megválasztott telepítési hely.</li>
        </ul>
      ),
    },
    {
      question: "13. Milyen adatokra/dokumentumokra készülj elő?",
      answer: (
        <ul>
          <li>Villanyszámla adatok (mérési pont azonosítók).</li>
          <li>Napelemes rendszer alapadatai (inverter típusa).</li>
          <li>Ingatlan alapadatok.</li>
          <li>Kivitelezői ajánlat / műszaki tartalom.</li>
        </ul>
      ),
    },
    {
      question: "14. Kell majd Ügyfélkapu / DÁP belépés?",
      answer:
        "Ezt majd a felhívás és a beadási felület mondja meg. Annyi biztos, hogy állami felületeknél gyakori az Ügyfélkapu+ vagy DÁP belépés.",
    },
    {
      question: "15. Mennyi lehet a teljes beruházás és az önerő?",
      answer:
        "A pontos ár a választott technikától, helyszíntől és a felhívás elszámolhatóságától függ. Sajtóhírben felmerült egy kb. 3,2M Ft-os csomagár becslés is, de a végleges költség mindig műszaki felmérés kérdése.",
    },
    {
      question:
        "16. Miért érdemes ezt a Green Island Invest Zrt.-vel csinálni?",
      answer:
        "Mert mi nem csak „akkut adunk”, hanem beadásbiztos, működő rendszert: előre ellenőrizzük a kompatibilitást (inverter–akku–mérés), és tételesen megmondjuk, mi kötelező, mi opcionális (pl. backup), és mi helyszínfüggő.",
    },
  ];

  const kisokosData = [
    {
      title: "Az Inverter: A rendszer „agya”",
      content:
        "Sokan azt hiszik, a napelem a legfontosabb, de a rendszer lelke valójában az inverter. A napelemek egyenáramot termelnek, de a háztartási gépek váltóárammal működnek. Az inverter végzi el ezt az átalakítást. A 2026-os pályázathoz hibrid invertert használunk, ami képes „beszélgetni” az akkumulátorral is.",
    },
    {
      title: "Az Akkumulátor: A házi „energiabank”",
      content:
        "Képzelje el az akkumulátort úgy, mint egy befőttesüveget, amibe elrakja a nyári napsütést „télire” (vagy a nappalit estére). Napközben, amikor Ön dolgozik, a napelemek termelnek. Tároló nélkül ezt az áramot fillérekért leadja a szolgáltatónak. Az akkumulátorral viszont elraktározza, és este a saját, ingyen áramát használja.",
    },
    {
      title: "Sziget üzem: Világítás áramszünetkor is",
      content:
        "A hagyományos napelemes rendszerek áramszünet esetén biztonsági okokból leállnak. A Green Island által kínált rendszerek azonban képesek a „Sziget üzemmódra”. Ha elmegy az áram az utcában, az Ön rendszere lekapcsolódik a hálózatról, és létrehoz egy saját „kis szigetet”.",
    },
    {
      title: "Túlfeszültség-védelem",
      content:
        "A központi elektromos hálózat néha kiszámíthatatlan. A modern hibrid rendszerek „pufferként” működnek. Simítják a feszültséget, és ha a hálózatból veszélyes ingadozás érkezik, a rendszer villámgyorsan beavatkozik, így megvédi az Ön értékes elektromos eszközeit.",
    },
    {
      title: "Optimális működés és szakértő tervezés",
      content:
        "Nem elég csak „felrakni” az eszközöket. Az akkumulátorok akkor élnek legtovább, ha optimálisan töltjük és sütjük ki őket. A Green Island mérnökei úgy állítják be a vezérlést, hogy az akkumulátor ne merüljön túl, és ne is legyen túlterhelve.",
    },
  ];

  const tocItems = [
    { id: "contact-form", label: "Jelentkezés" },
    { id: "mukodes", label: "Hogyan működik?" },
    { id: "kisokos", label: "Energiatároló Kisokos" },
    { id: "lehetoseg", label: "Miért most?" },
    { id: "ellenorzolista", label: "Ellenőrzőlista" },
    { id: "gyik", label: "Gyakran Ismételt Kérdések" },
    { id: "rolunk", label: "Miért mi?" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Structured Data for SEO (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: SEO_CONFIG.title,
    description: SEO_CONFIG.description,
    url: SEO_CONFIG.url,
    provider: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: COMPANY.shortName,
      legalName: COMPANY.fullName,
      url: SITE_URL,
      logo: COMPANY.logo,
      taxID: COMPANY.taxNumber,
      address: {
        "@type": "PostalAddress",
        streetAddress: `${COMPANY.address.street} (${COMPANY.address.lot})`,
        addressLocality: COMPANY.address.city,
        postalCode: COMPANY.address.postalCode,
        addressCountry: COMPANY.address.country,
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: COMPANY.mainPhone.replace(/\s/g, ""),
        email: COMPANY.mainEmail,
        availableLanguage: "Hungarian",
      },
    },
    mainEntity: {
      "@type": "Service",
      name: "Napelemes Akkumulátor Pályázat Tanácsadás 2026",
      description:
        "Segítség a 2026-os napelemes akkumulátor pályázat előkészítésében és beadásában. Akár 2,5 millió Ft állami támogatás otthoni energiatárolóra.",
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: {
        "@type": "Country",
        name: "Hungary",
      },
      offers: {
        "@type": "Offer",
        description:
          "Ingyenes előregisztráció a 2026-os akkumulátor pályázatra",
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Főoldal",
          item: "https://greenislandinvest.hu",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Akkumulátor Pályázat 2026",
          item: "https://greenislandinvest.hu/palyazat",
        },
      ],
    },
  };

  // FAQ Structured Data for rich snippets
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.slice(0, 10).map((item) => ({
      "@type": "Question",
      name: typeof item.question === "string" ? item.question : item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text:
          typeof item.answer === "string"
            ? item.answer
            : "Részletes válaszért látogasson el weboldalunkra.",
      },
    })),
  };

  return (
    <div className="palyazat">
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{SEO_CONFIG.title}</title>
        <meta name="title" content={SEO_CONFIG.title} />
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Hungarian" />
        <meta name="author" content="Green Island Invest Zrt." />
        <link rel="canonical" href={SEO_CONFIG.url} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CONFIG.url} />
        <meta property="og:title" content={SEO_CONFIG.title} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <meta property="og:image" content={SEO_CONFIG.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={SEO_CONFIG.siteName} />
        <meta property="og:locale" content={SEO_CONFIG.locale} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CONFIG.url} />
        <meta name="twitter:title" content={SEO_CONFIG.title} />
        <meta name="twitter:description" content={SEO_CONFIG.description} />
        <meta name="twitter:image" content={SEO_CONFIG.image} />

        {/* Additional SEO Tags */}
        <meta name="geo.region" content="HU" />
        <meta name="geo.placename" content="Hungary" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="revisit-after" content="7 days" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div
        className="palyazat__hero"
        style={{
          backgroundImage: `url(${require("../../assets/home/solarpanels-desktop.jpg")})`,
        }}>
        <div className="palyazat__hero__content">
          <motion.h1 initial="hidden" whileInView="visible" variants={fadeIn}>
            80% állami támogatás akkumulátorra 2026-ban
          </motion.h1>
          <motion.h2 initial="hidden" whileInView="visible" variants={fadeIn}>
            Ne maradj le róla!
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" variants={fadeIn}>
            A Green Island Invest Zrt. segít, hogy a lehető legtöbb támogatást
            hozd ki az akkurendszeredből – a megfelelő inverterrel, profi
            tervezéssel és teljes pályázati háttérrel.
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={fadeIn}
            style={{
              marginBottom: "1.5rem",
              fontWeight: "bold",
              textTransform: "uppercase",
              fontSize: "1.5rem",
              letterSpacing: "0.05em",
              color: "#6ee7b7", // Light emerald/mint color to stand out on dark bg
              textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              cursor: "pointer",
            }}
            onClick={() => scrollToSection("ugyintezes")}>
            LEVESSZÜK VÁLLÁRÓL A PÁLYÁZATTAL JÁRÓ ÜGYINTÉZÉST (részletek
            lejjebb) ▼
          </motion.div>

          <motion.div
            className="palyazat__hero__bullets"
            initial="hidden"
            whileInView="visible"
            variants={fadeIn}>
            <ul>
              <li>
                <span>✓</span>
                Akár 80% támogatás akkumulátoros beruházásra (kb. 2,5 millió Ft
                keretig).
              </li>
              <li>
                <span>✓</span>
                Személyre szabott akku + inverter javaslat a meglévő napelemhez.
              </li>
              <li>
                <span>✓</span>
                Pályázati előregisztráció, előminősítés, teljes adminisztrációs
                háttér igény szerint.
              </li>
            </ul>
          </motion.div>

          <motion.div
            className="hero-actions"
            initial="hidden"
            whileInView="visible"
            variants={fadeIn}>
            <button
              className="btn-primary"
              onClick={() =>
                document
                  .getElementById("contact-form")
                  .scrollIntoView({ behavior: "smooth" })
              }>
              Kérem az előminősítést és az önrészem kalkulációját
            </button>
            <a
              href={palyazatPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary">
              Pályázati kiírás megtekintése
            </a>
          </motion.div>
        </div>
      </div>
      {/* CTA Section with Form */}
      <div id="contact-form" className="palyazat__cta">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}
          style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2>Készen állsz, hogy ne maradj le a 2026-os akkupályázatról?</h2>
          <p>
            Ha most néhány percet rászánsz az előregisztrációra, akkor a Green
            Island Invest Zrt. a pályázat indulásakor már kész adatokkal,
            kidolgozott koncepcióval tudja képviselni az érdekeidet.
          </p>

          <AnimatePresence mode="wait">
            {submitStatus === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "24px",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  marginTop: "3rem",
                }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 10,
                    delay: 0.2,
                  }}
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "#10b981",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1.5rem",
                    fontSize: "2.5rem",
                    color: "white",
                  }}>
                  ✓
                </motion.div>
                <h3 style={{ color: "white", marginBottom: "1rem" }}>
                  Sikeres jelentkezés!
                </h3>
                <p
                  style={{
                    color: "rgba(255,255,255,0.9)",
                    marginBottom: "2rem",
                  }}>
                  Köszönjük bizalmát! Munkatársunk hamarosan felveszi Önnel a
                  kapcsolatot a megadott elérhetőségeken.
                </p>
                <button
                  className="btn-secondary"
                  onClick={() => setSubmitStatus(null)}
                  style={{
                    background: "transparent",
                    color: "white",
                    borderColor: "white",
                  }}>
                  Vissza az űrlaphoz
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="palyazat__form">
                <MultiStepContactForm
                  onSuccess={() => setSubmitStatus("success")}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      {/* Table of Contents */}
      <div className="palyazat__toc-container">
        <div className="palyazat__toc">
          <h3>Tartalomjegyzék</h3>
          <div className="palyazat__toc__grid">
            {tocItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="toc-btn">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Service Details Section (New) */}
      <div id="ugyintezes" className="palyazat__section">
        <motion.div
          className="palyazat__section__content"
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}>
          <h2 style={{ textTransform: "uppercase", textAlign: "center" }}>
            LEVESSZÜK VÁLLÁRÓL A PÁLYÁZATTAL JÁRÓ ÜGYINTÉZÉST
          </h2>
          <p
            style={{
              textAlign: "center",
              maxWidth: "800px",
              margin: "0 auto 3rem",
            }}>
            Igény esetén átvesszük az Otthoni Energiatároló Programhoz
            kapcsolódó regisztráció és pályázat beadásának teljes
            adminisztrációját. Célunk, hogy szakértői támogatással megkönnyítsük
            a pályázati folyamat elindítását, miközben a kivitelező
            kiválasztásának joga továbbra is kizárólag Önt illeti meg.
          </p>

          <div className="palyazat__section__grid">
            <div className="palyazat-card">
              <h3>Mit foglal magában a szolgáltatás?</h3>
              <ul>
                <li>
                  A pályázati regisztráció és a pályázat benyújtása az Ön
                  képviseletében
                </li>
                <li>
                  Segítség a pályázati lépések és követelmények érthetővé
                  tételében
                </li>
              </ul>
            </div>
            <div className="palyazat-card">
              <h3>
                Az ügyintézés ránk bízása nem jár semmilyen elköteleződéssel
              </h3>
              <ul>
                <li>
                  Nem kötelezi Önt arra, hogy minket válasszon kivitelezőként
                </li>
                <li>
                  A pályázat elbírálását és jogosultságát nem befolyásolja
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Section 2: How it works */}
      <div id="mukodes" className="palyazat__section">
        <motion.div
          className="palyazat__section__content"
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}>
          <h2>Hogyan működik az inverter + akkumulátor otthon?</h2>
          <p>
            A napelemek nappal termelnek. Az inverter alakítja át a panelekből
            érkező egyenáramot (DC) a háztartási hálózat számára használható
            váltóárammá (AC). Az akkumulátor ehhez kapcsolódva azt az energiát
            tárolja el, amit nappal nem használsz el azonnal – így este, éjjel,
            vagy áramszünet idején is saját árammal tudod működtetni az
            otthonodat.
          </p>

          <h3 style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            Milyen inverter alkalmas akkumulátorra?
          </h3>
          <div className="palyazat__section__grid">
            <div className="palyazat-card">
              <h3>Hibrid inverter</h3>
              <p>
                Kifejezetten akku kezelésére tervezett, külön
                akkumulátor-csatlakozással, töltésvezérléssel. Ideális, ha most
                korszerűsítesz vagy teljes rendszert építesz.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Meglévő, nem hibrid inverter</h3>
              <p>
                Ilyenkor speciális (AC-csatolt) akkumulátor vagy invertercsere
                („hibrid swap”) jön szóba – ezt típus és rendszer alapján kell
                eldönteni.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Meglévő hibrid rendszer</h3>
              <p>
                Ha már van hibrid invertered, gyakran „csak” új akkumodul(okat)
                kell illeszteni, kompatibilitás-ellenőrzés után.
              </p>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <button
              className="btn-secondary"
              onClick={() =>
                document
                  .getElementById("contact-form")
                  .scrollIntoView({ behavior: "smooth" })
              }>
              Kérjen ajánlatot most
            </button>
          </div>

          <div className="highlight-box">
            <p>
              „A Green Island Invest Zrt. minden esetben ellenőrzi az
              inverter–akku kompatibilitást, mielőtt ajánlatot ad – így
              elkerülhetők a drága műszaki zsákutcák.”
            </p>
          </div>
        </motion.div>
      </div>

      {/* Kisokos Section */}
      <div id="kisokos" className="palyazat__section palyazat__section--dark">
        <div className="palyazat__section__content">
          <h2>Napelemes Energiatároló Kisokos</h2>
          <p>Minden, amit tudni érdemes a modern, független otthonhoz.</p>
          <div className="palyazat__section__grid">
            {kisokosData.map((item, index) => (
              <motion.div
                key={index}
                className="palyazat-card"
                initial="hidden"
                whileInView="visible"
                variants={fadeIn}>
                <h3>{item.title}</h3>
                <p>{item.content}</p>
              </motion.div>
            ))}
          </div>
          <div style={{ textAlign: "center" }}>
            <button
              className="btn-secondary"
              onClick={() =>
                document
                  .getElementById("contact-form")
                  .scrollIntoView({ behavior: "smooth" })
              }>
              Érdekel a megoldás
            </button>
          </div>
        </div>
      </div>

      {/* Section 3: Why 2026 opportunity */}
      <div
        id="lehetoseg"
        className="palyazat__section palyazat__section--gradient">
        <div className="palyazat__section__content">
          <h2>Miért óriási lehetőség a 2026-os akkumulátor pályázat?</h2>
          <p>
            A várakozások szerint a 2026-ban induló program akár 80%-os
            támogatási intenzitást biztosíthat akkumulátoros beruházásokra,
            nagyjából 2,5 millió Ft-os támogatási plafonnal.
          </p>

          <div className="palyazat__section__grid">
            <motion.div
              className="palyazat-card"
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}>
              <h3>Esti és éjszakai áram saját forrásból</h3>
              <p>
                A nappal megtermelt energia nem „vész kárba” alacsony átvételi
                áron, hanem este, éjjel is felhasználható.
              </p>
            </motion.div>
            <motion.div
              className="palyazat-card"
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}>
              <h3>Áramszünet elleni védelem</h3>
              <p>
                Megfelelően tervezett rendszerrel a kritikus fogyasztók akkor is
                mennek, ha a hálózaton hiba van.
              </p>
            </motion.div>
            <motion.div
              className="palyazat-card"
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}>
              <h3>Jobb napelemes kihasználtság</h3>
              <p>
                Több saját termelést használsz el a saját házban, kevesebb
                energiát adsz át a hálózatnak.
              </p>
            </motion.div>
            <motion.div
              className="palyazat-card"
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}>
              <h3>Ingatlanérték növekedés</h3>
              <p>
                Egy korszerű, akkumulátorral kiegészített napelemes rendszer ma
                már komoly értéknövelő tényező.
              </p>
            </motion.div>
          </div>
          <div style={{ textAlign: "center" }}>
            <button
              className="btn-secondary"
              onClick={() =>
                document
                  .getElementById("contact-form")
                  .scrollIntoView({ behavior: "smooth" })
              }>
              Kérem a kalkulációt
            </button>
          </div>
        </div>
      </div>

      {/* Section 4: Checklist */}
      <div
        id="ellenorzolista"
        className="palyazat__section palyazat__section--dark">
        <motion.div
          className="palyazat__section__content"
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}>
          <h2>Pályázati előregisztrációs ellenőrzőlista</h2>
          <p>
            Minél felkészültebb vagy a pályázat indulásakor, annál nagyobb
            eséllyel tudsz gyorsan, hiánytalan anyagot beadni.
          </p>

          <div className="checklist-container">
            <div className="checklist-item">
              <h3>A) Admin alapok</h3>
              <ul>
                <li>Villanyszámla: mérési pont / azonosítók kéznél.</li>
                <li>Ki lesz a pályázó (tulajdonos / haszonélvező).</li>
                <li>Elérhetőségek rendben (telefon, email).</li>
              </ul>
            </div>
            <div className="checklist-item">
              <h3>B) Műszaki előszűrés (fotókkal)</h3>
              <ul>
                <li>Inverter adattábla (típus, teljesítmény).</li>
                <li>Inverter a környezetével (távolról).</li>
                <li>Villanyóra közelről (kijelző + típus).</li>
                <li>Mérőszekrény kívülről.</li>
                <li>Főelosztó nyitva (belső kép).</li>
                <li>Akku tervezett helye (2–3 szögből).</li>
              </ul>
            </div>
            <div className="checklist-item">
              <h3>C) Döntési kérdések</h3>
              <ul>
                <li>Szeretnél backupot áramszünetre?</li>
                <li>Hol lenne az akku? (garázs/gépészet/pince stb.)</li>
                <li>Van-e olyan nagy fogyasztó, ami este megy?</li>
              </ul>
            </div>
            <div className="checklist-item">
              <h3>D) „Beadásbiztos” csomag</h3>
              <ul>
                <li>Inverter alkalmas-e tárolóra / kell-e csere.</li>
                <li>Kell-e okosmérés / energiamenedzsment.</li>
                <li>Elosztóban van-e hely és megfelelő védelem.</li>
                <li>Ajánlat műszaki tartalma „érthető és tételes”.</li>
              </ul>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <button
              className="btn-secondary"
              onClick={() =>
                document
                  .getElementById("contact-form")
                  .scrollIntoView({ behavior: "smooth" })
              }>
              Segítséget kérek az előkészítésben
            </button>
          </div>

          <div className="highlight-box" style={{ marginTop: "30px" }}>
            <p>
              „Minél több adatot tudunk előre összegyűjteni, annál
              gördülékenyebb és gyorsabb lesz a pályázat beadása.”
            </p>
          </div>
        </motion.div>
      </div>

      {/* FAQ Section */}
      <div id="gyik" className="palyazat__section">
        <div className="palyazat__section__content">
          <h2>Gyakran Ismételt Kérdések (GYIK)</h2>
          <div className="faq-container">
            {faqData.map((item, index) => (
              <AccordionItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openAccordion === index}
                onClick={() => handleAccordionClick(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Section 5: Why Green Island */}
      <div id="rolunk" className="palyazat__section palyazat__section--dark">
        <div className="palyazat__section__content">
          <h2>Miért a Green Island Invest Zrt. a jó partner ehhez?</h2>
          <div className="palyazat__section__grid">
            <div className="palyazat-card">
              <h3>Szakértelem</h3>
              <p>
                Mi elmagyarázzuk, mit miért csinálunk. Nem csak eladunk egy
                dobozt, hanem beadásbiztos, működő rendszert építünk.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Biztonság</h3>
              <p>
                Minden rendszerünket túlfeszültség-védelemmel és a legmodernebb
                tűzvédelmi kapcsolókkal látjuk el.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Hosszú távú partner</h3>
              <p>
                Ha 5 év múlva kérdése van, mi akkor is itt leszünk és segítünk a
                finomhangolásban.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Pályázat-orientált csomagok</h3>
              <p>
                A cég csomagjai már a 2026-os pályázati logika szerint vannak
                felépítve – mind műszaki, mind pénzügyi oldalról.
              </p>
            </div>
            <div className="palyazat-card">
              <h3>Felkészültség a rohamra</h3>
              <p>
                Előregisztrált ügyfélkörrel dolgozunk. Amint megjelenik a
                pályázat, a már listán lévő ügyfelek anyaga gyorsan,
                strukturáltan kerül beadásra.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PalyazatModel;
